import React, { useCallback, useEffect, useState } from 'react';
import Panel from 'components/Panel';
import styled from 'styled-components';
import { BlueBorderButton, BlueButton } from 'components/Button';
import CurveThumbnail from 'components/CurveThumbnail';
import SectionHeader from 'components/Panels/SectionHeader';
import {
  CONTROL_TYPES,
  LOCAL_RESOURCE_MAPPED_VALUES,
  defaultItemsToDisplayInSingleRow,
  defaultRowHeight,
  defaultSecondaryRowHeight
} from 'utils/appConstants';
import {
  CurveThumbnailContainer,
  PanelButtonRow,
  PanelRowTextForm,
  StyledForm,
  PanelContent,
  FullPanel,
  SubPanel
} from 'components/PanelComponents';
import { determineBack } from 'utils/determineBack';
import { useDispatch, useSelector } from 'react-redux';
import { closeSidePanel, revertSidePanel } from 'reducers/sidePanel/actions';
import { fetchAllCurves } from 'api/curve';
import Table from 'components/Table';
import { formatCurveDataValues } from 'utils/format';

const CoordinatesContainer = styled.ul`
  margin: 0 auto;
`;

const CoordinateCoordinate = styled.li`
  padding: 2px;
`;

const curveColumnDefs = [
  {
    headerName: 'Curve',
    field: 'curveDataArray',
    maxWidth: 90,
    cellRenderer: ({ value }) => {
      if (!value) {
        return null;
      }
      return (
        <CurveThumbnailContainer>
          <CurveThumbnail curveData={value} />
        </CurveThumbnailContainer>
      );
    }
  },
  {
    headerName: 'Curve ID',
    field: 'curveId',
    maxWidth: 120
  },
  {
    headerName: 'Curve Points',
    field: 'curveDataArray',
    cellRenderer: row => {
      // Limit the amount of items to display
      const { curveDataArray = [], xMultiplier, yMultiplier } = row.data;
      const curveDataDisplayPoints =
        curveDataArray.length > defaultItemsToDisplayInSingleRow
          ? curveDataArray
              .slice(0, defaultItemsToDisplayInSingleRow)
              .concat(['...'])
          : curveDataArray;
      return (
        <CoordinatesContainer>
          {curveDataDisplayPoints.map((coords, i) => {
            const hasXValue = Object.prototype.hasOwnProperty.call(
              coords,
              'xvalue'
            );
            const hasYValue = Object.prototype.hasOwnProperty.call(
              coords,
              'yvalue'
            );
            if (hasXValue && hasYValue) {
              return (
                <CoordinateCoordinate key={i}>
                  ({formatCurveDataValues(coords.xvalue, xMultiplier)},{' '}
                  {formatCurveDataValues(coords.yvalue, yMultiplier)})
                </CoordinateCoordinate>
              );
            }
            return (
              <CoordinateCoordinate key={i}>{coords}</CoordinateCoordinate>
            );
          })}
        </CoordinatesContainer>
      );
    }
  }
];
const AddExistingCurve = ({ isActive }) => {
  const dispatch = useDispatch();
  const sidePanel = useSelector(state => state.sidePanel);
  const curveType = useSelector(state => state.sidePanel.data.curveType);
  const [searchInput, setSearchInput] = useState('');
  const [curvesData, setCurvesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCurve, setSelectedCurve] = useState(null);
  const [curveSelected, setCurveSelected] = useState(true);

  useEffect(() => {
    const curveDataMap = async () => {
      const curves = await fetchAllCurves();
      setCurvesData(curves);
      setIsLoading(false);
    };
    curveDataMap();
  }, []);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value;
    const name = target.name;
    if (name === 'searchInput') {
      setSearchInput(value);
    }
  };

  const onSelectCurve = useCallback(row => {
    const selectedCurve = row.data;
    selectedCurve !== null ? setCurveSelected(false) : setCurveSelected(true);
    setSelectedCurve(selectedCurve);
  }, []);

  const handleSubmit = e => {
    const { curveId } = selectedCurve;
    e.preventDefault();
    dispatch(revertSidePanel({ curveId }));
  };

  const getRowHeight = ({ data }) => {
    const { curveDataArray } = data;

    if (curveDataArray) {
      if (curveDataArray.length > defaultItemsToDisplayInSingleRow) {
        return (
          defaultRowHeight +
          defaultSecondaryRowHeight * defaultItemsToDisplayInSingleRow +
          30
        );
      }
      return (
        defaultRowHeight +
        defaultSecondaryRowHeight * (curveDataArray.length - 1) +
        30
      );
    }
    return defaultRowHeight;
  };

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    const curveTypeFilter = CONTROL_TYPES[curveType].curveType; // curve type we are specifically allowed to attach to this curve
    const filteredData = curvesData.filter(curve => {
      return (
        curve.curveId &&
        curve.localCurve !== LOCAL_RESOURCE_MAPPED_VALUES.REMOTE &&
        curve.curveId.toUpperCase().includes(searchInput.toUpperCase()) &&
        curve.curveType === curveTypeFilter
      );
    });

    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel title="New Control » Select Curve" primaryHeader />
          <PanelContent>
            <SubPanel>
              <SectionHeader
                header="Find Curve by ID"
                marginTop="12px"
                marginBottom="8px"
              />
              <PanelRowTextForm
                name="searchInput"
                onChange={handleInputChange}
                value={searchInput}
                autocomplete="off"
                placeholder="Search..."
              />

              <Table
                rowData={filteredData}
                columnDefs={curveColumnDefs}
                height="300px"
                width="100%"
                padding="0px"
                margin="0px"
                onRowClicked={onSelectCurve}
                getRowHeight={getRowHeight}
              />
            </SubPanel>
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() =>
                determineBack(
                  sidePanel,
                  () => dispatch(revertSidePanel()),
                  () => dispatch(closeSidePanel())
                )
              }
            >
              Cancel
            </BlueBorderButton>
            <BlueButton type="submit" disabled={curveSelected}>
              Select Curve
            </BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default AddExistingCurve;
